import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { AgentRole } from "@paperclip-cloud/shared";
import { useUiStore } from "../stores/ui";
import { useFleet } from "../hooks/useFleet";
import { FleetGrid } from "../components/FleetGrid";
import { AgentChatPanel } from "../components/AgentChatPanel";
import { AgentSwitcher } from "../components/AgentSwitcher";
import { PermissionPromptModal } from "../components/PermissionPromptModal";
import { StatusDot, workOrderTone } from "../components/StatusDot";
import { ActivityFeed } from "../components/ActivityFeed";
import { StatsCards } from "../components/StatsCards";
import { MembersPanel } from "../components/MembersPanel";
import { formatRelative } from "../lib/time";
import { agentHostsApi } from "../api/queries/agents";

const ROLE_LABEL: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Ops",
};

type RoleStatus = "ok" | "missing" | "offline";

export function DashboardPage() {
  const companyId = useUiStore((s) => s.activeCompanyId);
  const setPair = useUiStore((s) => s.setPairModalOpen);
  const fleet = useFleet(companyId);
  const [activeRole, setActiveRole] = useState<AgentRole>("ceo");

  // Per-role agent host configs. We need this to surface offline banners and
  // to badge devices in the fleet grid with which roles they host.
  const hostsQ = useQuery({
    queryKey: ["agent-hosts", companyId],
    queryFn: () => agentHostsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 10_000,
  });

  const statusFor = (role: AgentRole): RoleStatus => {
    const host = hostsQ.data?.agent_hosts.find((h) => h.role === role);
    if (!host || !host.enabled || !host.device_id) return "missing";
    const dev = fleet.data?.devices.find((d) => d.id === host.device_id);
    return dev?.online ? "ok" : "offline";
  };

  const ceoStatus = statusFor("ceo");
  const opsStatus = statusFor("ops");
  const ceoBad = ceoStatus !== "ok";
  const opsBad = opsStatus !== "ok";

  // Map device id → roles it hosts, for the fleet grid badges.
  const rolesByDeviceId: Record<string, AgentRole[]> = {};
  for (const h of hostsQ.data?.agent_hosts ?? []) {
    if (!h.device_id || !h.enabled) continue;
    (rolesByDeviceId[h.device_id] ??= []).push(h.role);
  }

  const recentOrders = (fleet.data?.active_orders ?? []).slice(0, 5);

  return (
    <main className="min-h-[calc(100vh-var(--topnav-h))] overflow-auto">
      <PermissionPromptModal />
      {companyId && (ceoBad || opsBad) && (
        <AgentHostBanner ceoStatus={ceoStatus} opsStatus={opsStatus} />
      )}

      {/* Toolbar — fleet header + Add computer */}
      <div className="flex h-[var(--toolbar-h)] items-center justify-between border-b border-border-subtle px-6">
        <div className="flex items-baseline gap-3">
          <h1 className="m-0 text-[18px] font-semibold tracking-[-0.01em]">Dashboard</h1>
          <span className="text-[12px] text-tertiary">
            {fleet.data?.devices.length ?? 0} computer
            {fleet.data?.devices.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setPair(true)}
            aria-keyshortcuts="n"
          >
            <Plus size={14} />
            Add computer
          </button>
        </div>
      </div>

      {/* Top: stats row (3 cards) */}
      {companyId && (
        <section className="px-6 pt-4">
          <StatsCards companyId={companyId} />
        </section>
      )}

      {/* Middle: fleet (left) + chat (right) + side strip (team + activity) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_var(--drawer-w)_320px] gap-4 px-6 pt-4">
        {/* Fleet column */}
        <section aria-label="Fleet" className="min-w-0">
          <h2 className="micro mb-2">Fleet</h2>
          <FleetGrid
            devices={fleet.data?.devices ?? []}
            loading={fleet.isLoading}
            rolesByDeviceId={rolesByDeviceId}
          />
        </section>

        {/* Chat column */}
        <section className="flex flex-col min-h-[420px] max-h-[640px] border border-border-subtle rounded-sm bg-surface overflow-hidden">
          <AgentSwitcher
            companyId={companyId}
            active={activeRole}
            onChange={setActiveRole}
          />
          <div className="min-h-0 flex-1">
            <AgentChatPanel companyId={companyId} role={activeRole} />
          </div>
        </section>

        {/* Side strip: team + activity (stacked) */}
        <aside className="flex flex-col gap-3 min-w-[280px]">
          {companyId && <MembersPanel companyId={companyId} />}
          {companyId && (
            <div className="card p-3 flex-1 min-h-[200px]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-medium tracking-wide uppercase text-tertiary">
                  Today's activity
                </h3>
                <Link
                  to="/activity"
                  className="text-[11px] text-accent hover:underline"
                >
                  View all →
                </Link>
              </div>
              <div className="overflow-auto max-h-[280px]">
                <ActivityFeed
                  companyId={companyId}
                  limit={15}
                  emptyMessage="Nothing yet today."
                />
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Bottom: recent work orders */}
      <section
        className="border-t border-border-subtle bg-surface px-6 py-3 mt-4"
        aria-label="Recent work orders"
      >
        <div className="flex items-center justify-between">
          <span className="micro">Recent work orders</span>
          <Link to="/work-orders" className="text-[12px] text-accent hover:underline">
            View all →
          </Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="mt-2 text-[12px] text-tertiary">
            No active orders. Ask the CEO to start one.
          </div>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {recentOrders.map((o) => {
              const tone = workOrderTone(o.status);
              return (
                <li key={o.id}>
                  <Link
                    to="/work-orders/$id"
                    params={{ id: o.id }}
                    className="card flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-raised"
                  >
                    <StatusDot tone={tone} pulse={tone === "running"} />
                    <span className="font-medium text-primary">{o.title}</span>
                    <span className="text-tertiary">·</span>
                    <span className="font-mono text-tertiary">{o.status}</span>
                    <span className="text-tertiary">·</span>
                    <span className="text-tertiary">{formatRelative(o.created_at)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}

function AgentHostBanner({
  ceoStatus,
  opsStatus,
}: {
  ceoStatus: RoleStatus;
  opsStatus: RoleStatus;
}) {
  const ceoBad = ceoStatus !== "ok";
  const opsBad = opsStatus !== "ok";

  // Combined banner if both bad — keep it terse.
  if (ceoBad && opsBad) {
    return (
      <div
        role="status"
        className="border-b border-status-blocked-soft bg-status-blocked-soft px-6 py-2 text-[12px] text-status-blocked"
      >
        <strong>{ROLE_LABEL.ceo} and {ROLE_LABEL.ops} hosts unavailable.</strong>{" "}
        Configure them in{" "}
        <Link to="/settings" className="underline">
          Settings
        </Link>{" "}
        and ensure both devices are online.
      </div>
    );
  }

  const role: AgentRole = ceoBad ? "ceo" : "ops";
  const status: RoleStatus = ceoBad ? ceoStatus : opsStatus;

  return (
    <div
      role="status"
      className="border-b border-status-blocked-soft bg-status-blocked-soft px-6 py-2 text-[12px] text-status-blocked"
    >
      {status === "missing" ? (
        <>
          <strong>No {ROLE_LABEL[role]} host configured.</strong> Pick a paired
          device in{" "}
          <Link to="/settings" className="underline">
            Settings
          </Link>{" "}
          before chatting with the {ROLE_LABEL[role]}.
        </>
      ) : (
        <>
          <strong>{ROLE_LABEL[role]} host offline.</strong> The host device must
          be online and running{" "}
          <code className="font-mono">paperclip-client start</code> to handle
          new chat messages.
        </>
      )}
    </div>
  );
}
